import { type ComponentType, useMemo } from "react";
import Select, { components, type GroupHeadingProps, type OptionProps } from "react-select";
import * as styles from "./styles.css";

export type Option<V = string | number> = {
  label: string;
  value: V;
};

export type GroupedOptions<T extends Option<string | number> = Option> = {
  label: string;
  options: T[];
};

export type SearchableSelectBoxProps<T extends Option<string | number> = Option> = {
  value: T["value"] | undefined;
  onChange: (value: T["value"] | undefined) => void;
  placeholder?: string;
  options: readonly T[] | readonly GroupedOptions<T>[];
  noOptionsMessage?: string;
  customOption?: ComponentType<OptionProps<T, false, GroupedOptions<T>>>;
  "aria-label"?: string;
  isDisabled?: boolean;
  instanceId?: string;
};

export const SearchableSelectBox = <T extends Option<string | number> = Option>({
  value,
  onChange,
  placeholder,
  options,
  noOptionsMessage,
  customOption,
  "aria-label": ariaLabel,
  isDisabled,
  instanceId,
}: SearchableSelectBoxProps<T>) => {
  const selected = useMemo(() => findSelectedOption(options, value), [options, value]);

  return (
    <div className={styles.selectOuter}>
      <Select<T, false, GroupedOptions<T>>
        value={selected}
        options={options}
        components={{
          Option: customOption ?? DefaultOption,
          GroupHeading,
        }}
        className={styles.select}
        classNamePrefix="react-select"
        placeholder={placeholder}
        noOptionsMessage={() => noOptionsMessage}
        isClearable
        onChange={(newValue) => onChange(newValue?.value)}
        aria-label={ariaLabel}
        isDisabled={isDisabled}
        instanceId={instanceId}
        menuShouldScrollIntoView={false}
      />
    </div>
  );
};

const DefaultOption = <T extends Option<string | number>>(
  props: OptionProps<T, false, GroupedOptions<T>>,
) => {
  const {
    data: { label },
  } = props;
  return (
    <components.Option {...props}>
      <div className={styles.optionInner}>
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const GroupHeading = <T extends Option<string | number>>({
  data,
}: GroupHeadingProps<T, false, GroupedOptions<T>>) => {
  const { label } = data;
  return <div className={styles.categoryHeader}>▼{label}</div>;
};

const findSelectedOption = <T extends Option<string | number>>(
  options: readonly T[] | readonly GroupedOptions<T>[],
  value: T["value"] | undefined,
): T | null => {
  if (value === undefined) return null;

  const flatOptions = options.filter((option): option is T => "value" in option);
  const groupedOptions = options.filter(
    (option): option is GroupedOptions<T> => "options" in option,
  );

  const selectedFlatOption = flatOptions.find((option) => option.value === value);
  if (selectedFlatOption) return selectedFlatOption;

  return (
    groupedOptions.flatMap((group) => group.options).find((option) => option.value === value) ||
    null
  );
};
